import React, {useContext, useCallback, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import {useClearForm, useFormPersistence} from 'components/forms/hooks';
import {withRouter} from 'react-router-dom';
import {Box, TextField} from '@material-ui/core';
import usePostBin from 'hooks/usePostBin';
import {useGetBin} from 'hooks/useGetEndpoint';
import {PSingleLine} from 'components/TypographyShortcuts';
import {LoadingPage} from 'components';
import {ErrorPage} from 'components/LoadingPage';
import {SuccessPage} from 'components/LoadingPage';
import {AppCtx} from 'context/AppContext';
import Show from 'components/utils/Show';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
    },
}));

const Prompt = ({
    open,
    title,
    description,
    modalClickCloses,
    onClose,
    buttonLeftText,
    buttonLeftColor = 'primary',
    onLeftClick,
    buttonRightText,
    buttonRightColor = 'primary',
    onRightClick,
    secretKey = false,
    enteredKey = null,
    setEnteredKey = () => {},
    keyError,
    children,
    ...rest
}) => {
    const classes = useStyles();

    return (
        <Dialog
            open={open}
            onClose={modalClickCloses && onClose}
            aria-labelledby='alert-dialog-title'
            aria-describedby='alert-dialog-description'
            {...rest}
        >
            <DialogTitle id='alert-dialog-title'>
                {title}
                <Show condition={!!onClose}>
                    <IconButton
                        aria-label='close'
                        className={classes.closeButton}
                        onClick={onClose}
                    >
                        <CloseIcon />
                    </IconButton>
                </Show>
            </DialogTitle>
            <DialogContent>
                <DialogContentText id='alert-dialog-description'>
                    {description}
                </DialogContentText>
                {secretKey && (
                    <TextField
                        value={enteredKey}
                        onChange={e => setEnteredKey(e.target.value)}
                        onKeyDown={e => e.code === 'Enter' && onRightClick()}
                        label='Confirm Secret Key'
                        autoFocus
                    />
                )}
                {children}
                {keyError ? (
                    <PSingleLine style={{color: 'red'}}>Incorrect key</PSingleLine>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={onLeftClick}
                    color={buttonLeftColor}
                    data-cy='prompt-button-left'
                >
                    {buttonLeftText}
                </Button>
                <Button
                    onClick={onRightClick}
                    color={buttonRightColor}
                    data-cy='prompt-button-right'
                    autoFocus
                >
                    {buttonRightText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

function StatePrompt({history, match}) {
    const start = `/forms/${match.url.split('/')[2]}/`;
    const {state, formState, node} = useFormPersistence();
    const {data} = useGetBin();
    const {post} = usePostBin();
    const [clearing, setClearing] = useState(false);
    const {formRedirect, setFormRedirect} = useContext(AppCtx);

    const [shouldPrompt, setShouldPrompt] = useState(
        state &&
            Object.keys(state).filter(key => !key.startsWith('_')).length > 0 &&
            !formRedirect
    );
    const clearForm = useClearForm();

    useEffect(() => {
        if (clearing) {
            // Persist state clear
            post({
                url: start,
                form_state_json: {
                    forms: {
                        ...formState,
                        [node]: {},
                    },
                },
            }).then(() => {
                // Navigate after state has been cleared
                history.push(start);
                setShouldPrompt(false);
                setClearing(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    useEffect(() => {
        if (formRedirect) {
            setTimeout(() => {
                setFormRedirect(false);
            }, 7000);
        }
        // eslint-disable-next-line
    }, [formRedirect]);

    const handleNew = () => {
        setClearing(true);
        clearForm();
    };

    const handleResume = () => {
        if (data?.url && data.url.match(new RegExp(node, 'i'))) {
            history.push(data.url);
        }
        setShouldPrompt(false);
    };

    const handleClose = useCallback(() => {
        setShouldPrompt(false);
    }, [setShouldPrompt]);

    const closeOnEsc = useCallback(
        e => {
            if (e.keyCode === 27) {
                handleClose();
            }
        },
        [handleClose]
    );

    // Allow bail out
    useEffect(() => {
        window.addEventListener('keyup', closeOnEsc);
        return () => {
            window.removeEventListener('keyup', closeOnEsc);
        };
    }, [closeOnEsc]);

    return (
        <Prompt
            open={shouldPrompt}
            title='You have existing form data'
            description='Would you like to resume the existing form or start a new form?'
            buttonLeftText='New'
            onLeftClick={handleNew}
            buttonRightText='Resume'
            onRightClick={handleResume}
            modalClickCloses
            onClose={handleClose}
        />
    );
}

function ConfirmPrompt({
    title = 'Confirm',
    description = 'Are you sure?',
    leftText = 'No',
    rightText = 'Yes',
    open,
    onConfirm,
    onCancel,
    children,
    ...rest
}) {
    const handleConfirm = () => {
        onConfirm && onConfirm();
    };

    const handleCancel = () => {
        onCancel && onCancel();
    };

    return (
        <Prompt
            open={open}
            title={title}
            description={description}
            onRightClick={handleConfirm}
            onLeftClick={handleCancel}
            buttonLeftText={leftText}
            buttonRightText={rightText}
            buttonRightColor='secondary'
            {...rest}
        >
            {children}
        </Prompt>
    );
}

const ConfirmPromptWithResult = ({
    title = 'Confirm',
    description = 'Are you sure?',
    leftText = 'No',
    rightText = 'Yes',
    open,
    onConfirm,
    onCancel,
    inProgress,
    success,
    successTitle = 'Success',
    successMessage = '',
    error,
    errorTitle = 'Error',
    errorMessage = '',
    children,
    ...rest
}) => {
    const handleConfirm = () => {
        onConfirm && onConfirm();
    };

    const handleCancel = () => {
        onCancel && onCancel();
    };

    return (
        <Prompt
            open={open}
            title={title}
            description={description}
            onRightClick={success || error ? handleCancel : handleConfirm}
            onLeftClick={success || error ? null : handleCancel}
            buttonLeftText={success || error ? '' : leftText}
            buttonRightText={success || error ? 'Close' : rightText}
            buttonRightColor='secondary'
            {...rest}
        >
            {inProgress ? (
                <Box height='250px' width='330px' margin='auto'>
                    <LoadingPage />
                </Box>
            ) : error ? (
                <Box padding={2} margin='auto'>
                    <ErrorPage title={errorTitle} message={errorMessage} />
                </Box>
            ) : success ? (
                <Box padding={2} height='250px' width='330px' margin='auto'>
                    <SuccessPage title={successTitle} message={successMessage} />
                </Box>
            ) : (
                children
            )}
        </Prompt>
    );
};

function ConfirmSecretPrompt({
    title,
    description,
    rightText,
    leftText,
    open,
    onConfirm,
    onCancel,
    secretKey = null,
}) {
    const [enteredKey, setEnteredKey] = useState(null);
    const [keyError, setKeyError] = useState(false);

    const handleConfirm = () => {
        onConfirm && enteredKey === secretKey ? onConfirm() : setKeyError(true);
    };

    const handleCancel = () => {
        onCancel && onCancel();
    };

    useEffect(() => {
        setKeyError(false);
    }, [enteredKey]);

    return (
        <Prompt
            open={open}
            title={title || 'Confirm'}
            description={description || 'Are you sure?'}
            onRightClick={handleConfirm}
            onLeftClick={handleCancel}
            buttonLeftText={leftText || 'No'}
            buttonRightText={rightText || 'Yes'}
            buttonRightColor='secondary'
            secretKey={true}
            enteredKey={enteredKey}
            setEnteredKey={setEnteredKey}
            keyError={keyError}
        />
    );
}

export default withRouter(StatePrompt);
export {Prompt, ConfirmPrompt, ConfirmSecretPrompt, ConfirmPromptWithResult};
